/* eslint-disable babel/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import * as React from "react";
import styled from "styled-components";

import { ActionBar, ActionBarProps } from "@origin-digital/ods-action-bar";
import { isChatClientSdkEnabled } from "@origin-digital/chat-client-sdk-launcher/chatClientAvailability";
import { EnvironmentNames } from "@origin-digital/platform-enums";
import { fireNewRelicPageAction } from "@origin-digital/reporting-client";

import {
  AlertBanner,
  AlertBannerProps,
  Hidden,
  MarkdownLite,
  Text,
} from "@origin-digital/ods-core";

import {
  eventListener,
  IActionBarUpdate,
  IAlertBanner,
  IAlertBannerClose,
  IOriginEvents,
  ISubscription,
  navigationBack,
  navigationClose,
  openChatClient,
  TOPICS,
} from "@origin-digital/event-dispatcher";
import {
  FEATURE_FLAGS,
  getDefaultFeatures,
  Z_INDEX,
} from "@origin-digital/tal-client-common";
import {
  CrisisBanner,
  PageManifestCrisisBanner,
} from "@origin-digital/crisis-banner";
import { PropertySelector } from "../PropertySelector/PropertySelector";
import { ServiceSelector } from "../ServiceSelector/ServiceSelector";

export type AlertStatusLabels =
  | "basic"
  | "error"
  | "info"
  | "success"
  | "warning";

interface AlertBannerContentProps {
  visible: boolean;
  message: string;
  heading?: string;
  type?: AlertStatusLabels;
  uniqueId?: string;
  hideCloseButton?: boolean;
}

export interface HeaderProps extends ActionBarProps {
  hideActionBar?: boolean | "desktop";
  isNative?: boolean;
  crisisBanner?: PageManifestCrisisBanner;
}

interface HeaderState {
  actionBar: ActionBarProps;
  alertBanner: AlertBannerContentProps;

  actionBarUpdateUnsubscribe?: ISubscription;
  alertBannerOpenUnsubscribe?: ISubscription;
  alertBannerCloseUnsubscribe?: ISubscription;
}

const mapAlertBannerType = (
  oldType?: AlertStatusLabels
): AlertBannerProps["tone"] => {
  if (oldType === "error") {
    return "critical";
  } else if (oldType === "warning") {
    return "caution";
  } else if (oldType === "success") {
    return "positive";
  } else if (oldType === "basic") {
    return "neutral";
  }
  return "info";
};

const StickyContainer = styled.div`
  z-index: ${Z_INDEX.HEADER};
  position: sticky;
  top: 0;
`;

const AlertRelativeAnchor = styled.div`
  position: relative;
`;

const AlertContainer = styled.div<{ $visible: boolean }>`
  pointer-events: ${(p) => (p.$visible ? "unset" : "none")};
  position: absolute;
  width: 100%;

  /* disable transition out as the content will be gone from the state
   * once the close button is pressed */
  transition: all 0.2s ease-in-out;
  transition: ${(p) =>
    p.$visible
      ? // Ease both opacity and transform when opening
        "all 0.2s ease-in-out"
      : // Ease the opacity out, but don't animate away the translateY
        // The linear function looks like this _| so the transform will
        // only happen at the end of the animation, when the opacity is
        // already at 0 (setting it up for the ease in when it appears)
        "opacity 0.2s ease-in-out, transform 0.2s linear(0 0%, 0 100%, 1 100%)"};
  opacity: ${(p) => (p.$visible ? "1" : "0")};
  transform: translateY(${(p) => (p.$visible ? "0" : `-10px`)});

  & > div[data-id="alert-banner"] {
    justify-content: center;
  }
`;

const BottomLine = styled.div`
  border-bottom: 1px solid ${(p) => p.theme.palette.borderColor.primary};
`;

export class Header extends React.Component<HeaderProps, HeaderState> {
  constructor(props: HeaderProps, state: HeaderState) {
    super(props, state);
    const {
      title,
      navButton,
      headingComponent,
      desktopVariant,
      showPropertySelector,
      showServiceSelector,
      showAllProperties,
      secondaryColor,
    } = props;
    this.state = {
      actionBar: {
        title,
        navButton,
        headingComponent,
        desktopVariant,
        showPropertySelector,
        showServiceSelector,
        showAllProperties,
        secondaryColor,
        showChatButton: false,
      },
      alertBanner: {
        visible: false,
        hideCloseButton: false,
        message: "",
      },
    };
    this.handleOnCloseAlertBanner = this.handleOnCloseAlertBanner.bind(this);
    this.handleOnClickChatButton = this.handleOnClickChatButton.bind(this);
  }

  componentDidMount() {
    const actionBarUpdateUnsubscribe = eventListener.addListener(
      TOPICS.ACTION_BAR_UPDATE,
      (topic: IOriginEvents) => {
        if (topic.topic === TOPICS.ACTION_BAR_UPDATE) {
          this.handleActionBarUpdate(topic);
        }
      }
    );

    const alertBannerOpenUnsubscribe = eventListener.addListener(
      TOPICS.ALERT_BANNER_OPEN,
      (topic: IOriginEvents) => {
        if (topic.topic === TOPICS.ALERT_BANNER_OPEN) {
          this.handleAlertBannerOpen(topic);
        }
      }
    );

    const alertBannerCloseUnsubscribe = eventListener.addListener(
      TOPICS.ALERT_BANNER_CLOSE,
      (topic: IOriginEvents) => {
        if (topic.topic === TOPICS.ALERT_BANNER_CLOSE) {
          this.handleAlertBannerClose(topic);
        }
      }
    );

    const environment = window.oetal?.env?.name as EnvironmentNames | undefined;
    const showChatButton = isChatClientSdkEnabled({
      featureEnabled:
        getDefaultFeatures(environment)[FEATURE_FLAGS.CHAT_CLIENT_SDK],
      routeEnabled: window.oetal?.pageData?.enableChatClientSdk,
    });

    this.setState((prevState) => ({
      actionBar: { ...prevState.actionBar, showChatButton },
      actionBarUpdateUnsubscribe,
      alertBannerOpenUnsubscribe,
      alertBannerCloseUnsubscribe,
    }));

    // "desktop" only CSS-hides the bar, so the button still renders.
    if (showChatButton && this.props.hideActionBar !== true) {
      fireNewRelicPageAction("chatButtonImpression");
    }
  }

  componentWillUnmount() {
    this.state.actionBarUpdateUnsubscribe &&
      this.state.actionBarUpdateUnsubscribe();
    this.state.alertBannerOpenUnsubscribe &&
      this.state.alertBannerOpenUnsubscribe();
    this.state.alertBannerCloseUnsubscribe &&
      this.state.alertBannerCloseUnsubscribe();
  }

  handleActionBarUpdate(topic: IActionBarUpdate) {
    this.setState((prevState) => {
      const { propertySelector, mobileNavButton, ...payloadOther } =
        topic.payload;
      const { enabled: showPropertySelector, ...propertySelectorOther } =
        propertySelector || {};
      const navButton =
        payloadOther.navButton ??
        (mobileNavButton ? { mobile: mobileNavButton } : undefined);

      return {
        actionBar: {
          ...prevState.actionBar,
          ...payloadOther,
          ...propertySelectorOther,
          ...(showPropertySelector !== undefined && { showPropertySelector }),
          ...(navButton !== undefined && { navButton }),
        },
      };
    });
  }

  handleAlertBannerOpen(topic: IAlertBanner) {
    this.setState({
      alertBanner: {
        visible: true,
        ...topic.payload,
      },
    });
  }

  handleAlertBannerClose(topic: IAlertBannerClose) {
    if (
      this.state.alertBanner &&
      this.state.alertBanner.uniqueId === topic.payload.uniqueId
    ) {
      this.handleOnCloseAlertBanner();
    }
  }

  handleOnClickMobileNavButton() {
    const mobileButton = this.state.actionBar.navButton?.mobile;
    const buttonType = this.props.isNative
      ? mobileButton
      : this.state.actionBar.navButton?.desktop ?? mobileButton;

    if (buttonType === "close") {
      navigationClose();
    } else if (typeof window !== "undefined") {
      navigationBack();
    }
  }

  handleOnCloseAlertBanner() {
    this.setState((state) => ({
      alertBanner: { ...state.alertBanner, visible: false },
    }));
  }

  handleOnClickChatButton() {
    fireNewRelicPageAction("chatButtonClick");
    openChatClient().catch(() => {
      // Keep the action available so the customer can retry.
    });
  }

  render() {
    const { hideActionBar, isNative } = this.props;
    const { actionBar, alertBanner } = this.state;
    const breakpointToHide = hideActionBar === "desktop" ? "lg" : undefined;
    const showActionBar = hideActionBar === "desktop" ? true : !hideActionBar;
    const crisisBanner = this.props.crisisBanner || "none";

    const actionBarContent = showActionBar ? (
      <Hidden above={breakpointToHide}>
        <ActionBar
          navButton={actionBar.navButton}
          headingComponent={actionBar.headingComponent}
          desktopVariant={actionBar.desktopVariant}
          onClickMobileNavButton={() => this.handleOnClickMobileNavButton()}
          title={actionBar.title}
          showPropertySelector={actionBar.showPropertySelector}
          propertySelector={
            <PropertySelector
              showAllProperties={actionBar.showAllProperties}
              showServiceSelector={actionBar.showServiceSelector}
            />
          }
          showServiceSelector={actionBar.showServiceSelector}
          serviceSelector={<ServiceSelector />}
          onClickChatButton={this.handleOnClickChatButton}
          secondaryColor={actionBar.secondaryColor}
          showChatButton={actionBar.showChatButton}
        />
        <BottomLine className="accelerated-element" />
      </Hidden>
    ) : null;

    const alertBannerContent = (
      <AlertRelativeAnchor>
        <AlertContainer $visible={alertBanner.visible}>
          {/* We have to set defaults for these mandatory fields so that
              the element can already exist in the dom (hidden) and we
              can then transition it */}
          <AlertBanner
            title={alertBanner?.heading || ""}
            tone={mapAlertBannerType(alertBanner?.type || "info")}
            onCloseClick={
              this.state.alertBanner.hideCloseButton
                ? undefined
                : this.handleOnCloseAlertBanner
            }
          >
            <Text>
              <MarkdownLite>{alertBanner?.message || ""}</MarkdownLite>
            </Text>
          </AlertBanner>
        </AlertContainer>
      </AlertRelativeAnchor>
    );

    return (
      <StickyContainer>
        {actionBarContent}
        {isNative ? <CrisisBanner position={crisisBanner} /> : null}
        {alertBannerContent}
      </StickyContainer>
    );
  }
}
